import { useState } from 'react';

import { useShowMore } from 'hooks';
import { ButtonColors } from 'types';
import { mapQuery } from 'utils/query';
import { Icon } from 'components/icon';
import { List } from 'components/list';
import { formatDate } from 'utils/date';
import { Button } from 'components/button';
import { ListCard } from 'components/list-card';
import { ListHeader } from 'components/list-header';
import { formatPhone, formatPrice } from 'utils/helpers';
import {
  EventTeamMember,
  useDetailsQuery,
  useEmployeeCommissionsInfoQuery,
} from 'store/api/event';

import * as Styles from './styles';
import { CommissionsProps } from './types';
import { CommissionDetailsDialog } from './dialog';
import { employeeConfig, commissionTitles } from './config';

export const Commissions = ({ event }: CommissionsProps) => {
  const [selectedEmployee, setSelectedEmployee] = useState<EventTeamMember>();

  const { data: eventDetails, ...eventProps } = useDetailsQuery(
    String(event.id),
    { skip: !event.id }
  );

  const { data, ...props } = useEmployeeCommissionsInfoQuery(
    { id: event.id, userId: selectedEmployee?.id || 0 },
    { skip: !selectedEmployee }
  );

  const eventEmployees = [
    ...(eventDetails?.teamMembers || []),
    eventDetails?.leader,
  ].filter(Boolean);

  const { visibleData, ...showMoreProps } = useShowMore(data?.commissions);

  if (data && selectedEmployee) {
    return (
      <List
        {...mapQuery(props)}
        {...showMoreProps}
        count={visibleData?.length}
        header={
          <>
            <Styles.HeaderButtonsContainer>
              <Button
                transparent
                padding="0"
                icon={<Icon.ChevronLeft />}
                text={selectedEmployee?.name}
                colorType={ButtonColors.BlackWhite}
                onClick={() => setSelectedEmployee(undefined)}
              />

              <CommissionDetailsDialog
                data={data}
                selectedEmployee={selectedEmployee}
              />
            </Styles.HeaderButtonsContainer>

            <ListHeader titles={commissionTitles} />
          </>
        }
      >
        {visibleData?.map((commission, index) => (
          <ListCard
            key={index}
            headers={commissionTitles}
            values={[
              formatDate(commission.date),
              formatPrice(commission.grossSale),
              formatPrice(commission.netSale),
              formatPrice(commission.commissions),
              formatPrice(commission.tip),
              formatPrice(commission.bonuses),
              formatPrice(commission.advances),
            ]}
          />
        ))}
      </List>
    );
  }

  return (
    <List
      {...mapQuery(eventProps)}
      count={eventEmployees.length}
      mainWrapperStyles={{ padding: 0 }}
      header={
        <ListHeader
          padding="8px"
          titles={employeeConfig.titles}
          template={employeeConfig.template}
        />
      }
    >
      {eventEmployees?.map((value) => (
        <ListCard
          key={value?.id}
          headers={employeeConfig.titles}
          template={employeeConfig.template}
          cellsMobile={employeeConfig.cellsMobile}
          onClick={() => setSelectedEmployee(value)}
          values={[
            value?.name,
            value?.email,
            formatPhone(value?.employee?.phoneNumber),
            value?.role?.name,
          ]}
        />
      ))}
    </List>
  );
};
